import React, { useContext, useMemo, useState } from "react";
import styled, { css, useTheme } from "styled-components";
import {
  AnalyticsPageName,
  BacktestingRunType,
  BotType,
  LanguageNameSpaces,
  MarginTypes,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { BacktestDetailsContext } from "@shared/features/bots/BacktestDetails.context";
import {
  useMostTradedCoinsStatistic,
  usePnlStatusStatistic,
} from "@shared/features/bots/BotForm/Backtesting/hooks";
import useTargetsStatistic from "@shared/features/bots/BotForm/Backtesting/useTargetsStatistic";
import {
  BacktestingPnlStatus,
  TargetsTabs,
} from "@shared/features/bots/BotForm/common/types";
import {
  AVG_TRADE_DURATION_DRAWDOWN_HELPER_PROPS,
  BACKTESTING_MAX_DRAWDOWN_HELPER_PROPS,
} from "@shared/features/bots/BotForm/common/constants";
import { PERCENT_PRECISION } from "@shared/features/tradingTerminal/common/constants";
import { toFixed } from "@shared/utils/numbers";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import Panel from "common/NewLook/Panel.tsx";
import SegmentTabs from "common/NewLook/SegmentTabs.tsx";
import StatStrip from "common/NewLook/StatStrip.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import AllocationDonut from "common/NewLook/AllocationDonut";
import PanelEmpty from "common/Empty/PanelEmpty.tsx";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import { formatDuration } from "features/bots/BotForm/Backtesting/NewLook/format";

interface BacktestStatisticsProps {
  /** One column (the drawer's side extension) instead of a two-up grid. */
  narrow?: boolean;
  loading?: boolean;
}

/**
 * New look: a backtest's statistics — the key figures in one strip (no icon
 * tiles), then panels for how the trades ended, what was traded and which
 * orders filled.
 */
const BacktestStatistics = ({ narrow, loading }: BacktestStatisticsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { liveBacktestingData: run, botData } = useContext(
    BacktestDetailsContext
  )!;
  const showMostTradedCoins = botData?.bot_type === BotType.SIGNAL;

  const marginSplit = useMemo(() => {
    const counts = run?.margin_type_to_counter;
    if (!counts) return undefined;
    const isolated = counts[MarginTypes.ISOLATED] || 0;
    const cross = counts[MarginTypes.CROSS] || 0;
    const total = isolated + cross;
    if (!total) return undefined;
    return [
      [t("margin_types.isolated"), isolated],
      [t("margin_types.cross"), cross],
    ]
      .filter(([, count]) => Number(count) > 0)
      .map(
        ([name, count]) =>
          `${name} ${Math.round((Number(count) / total) * 100)}%`
      )
      .join(" · ");
  }, [run?.margin_type_to_counter, t]);

  const skeleton = <Skeleton width={72} height={22} />;
  const strip = (
    <Strip
      $narrow={narrow}
      items={[
        {
          key: "trades",
          label: t("backtesting.tabs.trades"),
          value: loading ? skeleton : run?.number_of_trades ?? "—",
        },
        {
          key: "drawdown",
          label: (
            <LabelWithHelp>
              {t("backtesting.statistics.max_drawdown")}
              <HelperTooltip
                {...BACKTESTING_MAX_DRAWDOWN_HELPER_PROPS}
                description={t(
                  BACKTESTING_MAX_DRAWDOWN_HELPER_PROPS.description
                )}
                title={t(BACKTESTING_MAX_DRAWDOWN_HELPER_PROPS.title)}
              />
            </LabelWithHelp>
          ),
          value: loading
            ? skeleton
            : run?.max_drawdown
            ? `${toFixed(run.max_drawdown * 100, PERCENT_PRECISION)}%`
            : "—",
        },
        {
          key: "duration",
          label: (
            <LabelWithHelp>
              {t("backtesting.statistics.avg_trade_duration")}
              <HelperTooltip
                {...AVG_TRADE_DURATION_DRAWDOWN_HELPER_PROPS}
                description={t(
                  AVG_TRADE_DURATION_DRAWDOWN_HELPER_PROPS.description
                )}
                title={t(AVG_TRADE_DURATION_DRAWDOWN_HELPER_PROPS.title)}
              />
            </LabelWithHelp>
          ),
          value: loading ? skeleton : formatDuration(run?.avg_trade_time),
        },
        {
          key: "leverage",
          label: t("backtesting.statistics.leverage"),
          value: loading
            ? skeleton
            : run?.avg_multiplier
            ? `${toFixed(run.avg_multiplier, 1)}x`
            : "—",
          meta: !loading && marginSplit,
        },
      ]}
    />
  );

  return (
    <Wrapper>
      {strip}
      <Panels $narrow={narrow} $three={showMostTradedCoins}>
        <PnlStatusPanel loading={loading} />
        {showMostTradedCoins && <MostTradedCoinsPanel loading={loading} />}
        <TargetsPanel run={run} loading={loading} />
      </Panels>
    </Wrapper>
  );
};

export default React.memo(BacktestStatistics);

const PNL_STATUS_ORDER = [
  BacktestingPnlStatus.WINNING,
  BacktestingPnlStatus.NEUTRAL,
  BacktestingPnlStatus.LOSING,
];

/** How the trades ended: one split bar (green, grey, red) and its legend. */
const PnlStatusPanel = ({ loading }: { loading?: boolean }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const tokens = getNewLookTokens(useTheme());
  const { pieChartData, isEmpty, helperProps } = usePnlStatusStatistic();

  const items = useMemo(() => {
    const byName = new Map(pieChartData.map((point) => [point.name, point]));
    return PNL_STATUS_ORDER.map((status) => ({
      status,
      share: Number(byName.get(status)?.value || 0),
      count: Number(byName.get(status)?.originalValue || 0),
    })).filter((item) => item.count > 0);
  }, [pieChartData]);
  const total = items.reduce((sum, item) => sum + item.count, 0);
  const colorOf = (status: BacktestingPnlStatus) =>
    status === BacktestingPnlStatus.WINNING
      ? tokens.up
      : status === BacktestingPnlStatus.LOSING
      ? tokens.down
      : tokens.muted;

  return (
    <Panel
      title={t("backtesting.statistics.pnl_status")}
      extra={<HelperTooltip {...helperProps} />}
      actions={
        !loading && total ? (
          <HeaderMeta>
            {t("backtesting.statistics.num_trades", { num: total })}
          </HeaderMeta>
        ) : undefined
      }
    >
      {loading ? (
        <Skeleton width="100%" height={96} radius={8} />
      ) : isEmpty ? (
        <PanelEmpty title={t("empty_states.no_status")} />
      ) : (
        <>
          <SplitBar aria-hidden={true}>
            {items.map((item) => (
              <span
                key={item.status}
                style={{
                  flexGrow: item.count,
                  background: colorOf(item.status),
                }}
              />
            ))}
          </SplitBar>
          <Legend>
            {items.map((item) => (
              <LegendRow key={item.status}>
                <LegendName>
                  <Dot style={{ background: colorOf(item.status) }} />
                  {t(`backtesting.statistics.${item.status}`)}
                </LegendName>
                <LegendCount>
                  {t("backtesting.statistics.num_trades", {
                    num: item.count,
                  })}
                </LegendCount>
                <LegendShare>{item.share.toFixed(2)}%</LegendShare>
              </LegendRow>
            ))}
          </Legend>
        </>
      )}
    </Panel>
  );
};

/** Signals bots: which coins the group's signals traded most. */
const MostTradedCoinsPanel = ({ loading }: { loading?: boolean }) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { pieChartData, isEmpty } = useMostTradedCoinsStatistic();
  const { liveBacktestingData } = useContext(BacktestDetailsContext) || {};
  const total = Object.values(
    liveBacktestingData?.asset_to_counter || {}
  ).reduce((sum, value) => sum + value, 0);

  return (
    <Panel title={t("statistics.most_traded_coins")}>
      {loading ? (
        <Skeleton width="100%" height={112} radius={8} />
      ) : isEmpty ? (
        <PanelEmpty title={t("empty_states.no_coins")} />
      ) : (
        <AllocationDonut
          bare={true}
          compact={true}
          chartId="mostTradedCoinsDonut"
          data={pieChartData}
          total={total}
        />
      )}
    </Panel>
  );
};

interface TargetsPanelProps {
  run?: BacktestingRunType;
  loading?: boolean;
}

/** Which entries and take-profits filled, as a share of the trades. */
const TargetsPanel = ({ run, loading }: TargetsPanelProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const tokens = getNewLookTokens(useTheme());
  const { tab, setTab, getTabContent } = useTargetsStatistic({ data: run });
  const [expanded, setExpanded] = useState(false);
  const { mainContent, collapsedContent } = getTabContent(tab);
  const rows = expanded
    ? [...mainContent, ...(collapsedContent || [])]
    : mainContent;
  const isEntries = tab === TargetsTabs.ENTRIES;
  const targetType = isEntries
    ? t("backtesting.statistics.entry")
    : t("backtesting.statistics.take_profit");

  return (
    <Panel
      title={t("backtesting.statistics.filled_targets")}
      extra={
        <HelperTooltip
          description={t("backtesting.helper_tooltips.targets", {
            targetType: targetType.toLowerCase(),
          })}
          analyticsFieldName={isEntries ? "Entries" : "Take Profits"}
          analyticsPageName={AnalyticsPageName.BACKTESTING}
          articleId="9575281"
        />
      }
      actions={
        <SegmentTabs<TargetsTabs>
          options={[TargetsTabs.ENTRIES, TargetsTabs.TAKE_PROFITS]}
          value={tab}
          onChange={(next) => {
            setExpanded(false);
            setTab(next);
          }}
          ariaLabel={t("backtesting.statistics.filled_targets")}
          getLabel={(option) =>
            option === TargetsTabs.ENTRIES
              ? t("backtesting.statistics.entries")
              : t("backtesting.statistics.take_profits")
          }
        />
      }
    >
      {loading ? (
        <Skeleton width="100%" height={96} radius={8} />
      ) : !rows.length ? (
        <PanelEmpty title={t("empty_states.no_data")} />
      ) : (
        <>
          <Targets>
            {rows.map(([target, count]) => {
              const share = run?.number_of_trades
                ? Math.min(100, (count / run.number_of_trades) * 100)
                : 0;
              return (
                <Target key={`${tab}_${target}`}>
                  <TargetHead>
                    <TargetName>
                      {targetType} {target}
                    </TargetName>
                    <TargetCount>
                      {t("backtesting.statistics.num_trades", { num: count })}
                    </TargetCount>
                    <TargetShare>{Math.floor(share)}%</TargetShare>
                  </TargetHead>
                  <Track aria-hidden={true}>
                    <Fill
                      style={{
                        width: `${share}%`,
                        background: isEntries ? tokens.up : tokens.down,
                      }}
                    />
                  </Track>
                </Target>
              );
            })}
          </Targets>
          {collapsedContent && (
            <MoreButton type="button" onClick={() => setExpanded(!expanded)}>
              {expanded
                ? tCommon("filters.common.show_less")
                : `${tCommon("filters.common.show_more")} (${
                    collapsedContent.length
                  })`}
            </MoreButton>
          )}
        </>
      )}
    </Panel>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;
const Strip = styled(StatStrip)<{ $narrow?: boolean }>`
  ${(p) =>
    p.$narrow &&
    css`
      && {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    `}
`;
const LabelWithHelp = styled.span`
  display: inline-flex;
  align-items: center;
`;
const Panels = styled.div<{ $narrow?: boolean; $three?: boolean }>`
  display: grid;
  grid-template-columns: ${(p) =>
    p.$narrow ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))"};
  gap: 16px;
  ${(p) =>
    !p.$narrow &&
    p.$three &&
    css`
      > :last-child {
        grid-column: 1 / -1;
      }
    `}
  @media (max-width: 700px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const HeaderMeta = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
`;
const SplitBar = styled.div`
  display: flex;
  gap: 2px;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  > span {
    flex-basis: 0;
    min-width: 2px;
  }
`;
const Legend = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 12px;
`;
const LegendRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto 64px;
  align-items: center;
  gap: 12px;
  min-height: 32px;
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
const LegendName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).text};
  white-space: nowrap;
`;
const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: none;
`;
const LegendCount = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  text-align: right;
  white-space: nowrap;
`;
const LegendShare = styled.span`
  ${newLookType.figure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  text-align: right;
`;
const Targets = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const Target = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const TargetHead = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto 48px;
  align-items: baseline;
  gap: 12px;
`;
const TargetName = styled.span`
  ${newLookType.body};
  color: ${(p) => getNewLookTokens(p.theme).text};
  white-space: nowrap;
`;
const TargetCount = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
`;
const TargetShare = styled.span`
  ${newLookType.figure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  text-align: right;
`;
const Track = styled.div`
  height: 4px;
  border-radius: 2px;
  background: ${(p) => getNewLookTokens(p.theme).hairlineStrong};
  overflow: hidden;
`;
const Fill = styled.div`
  height: 100%;
  border-radius: 2px;
  transition: width 300ms ease;
`;
const MoreButton = styled.button`
  margin: 12px 0 0 -8px;
  height: 28px;
  padding: 0 8px;
  border: none;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: transparent;
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).accent};
  font-family: inherit;
  cursor: pointer;
  &:hover {
    background: ${(p) => getNewLookTokens(p.theme).rowHover};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 1px;
  }
`;
